import {
  ActionHandler,
  BaseSeqState,
  PickMainState,
  GenerateActionTypes,
  BLC_STATUS_TYPE,
  // BLC_STATUS_TYPE,
  // MasterMapOptions,
} from "../../../../src/react-lib/apps/HISV3/common/CommonInterface";
import WasmController from "@/react-lib/frameworks/WasmController";
import CONFIG from "config/config";

/*================================ SERIALIZERS ==============================*/

import DiagRuleSerializer from "issara-sdk/types/DiagRuleSerializer_apps_PRX";
import PublishedDiagRuleNodeSerializer from "issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";
import BroadcastPostSerializer from "issara-sdk/types/BroadcastPostSerializer_apps_PRX";
import EmployeeSerializer from "issara-sdk/types/EmployeeSerializer_users";
import UserProfileSerializer from "issara-sdk/types/UserProfileSerializer_users";

/*================================ FIREBASE ==============================*/

import { FieldValue, Timestamp } from "firebase/firestore";
import FormCategorySerializer from "@/issara-sdk/types/FormCategorySerializer_apps_PRX";

/*================================ ACTIONS & CONSTANTS ==============================*/

// --- action const
export const ACTIONS = {
  FETCH: "FETCH",

  // *-- List Page
  ADD_FORM: "ADD_FORM",
  GET_FORM: "GET_FORM",
  ACTION_FORM: "ACTION_FORM",
  DOWNLOAD_RAW: "DOWNLOAD_RAW",

  // *-- Detail Page
  PUBLISH_FORM: "PUBLISH_FORM",
  SAVE_FORM: "SAVE_FORM",
  EDIT_FORM: "EDIT_FORM",
  GET_BROADCAST_NODE_LIST: "GET_BROADCAST_NODE_LIST",
  GET_POST_CALCULATE_CREDIT: "GET_POST_CALCULATE_CREDIT",
  POST_BROADCAST: "POST_BROADCAST",
  GET_UPDATED_BY_USER_FIREBASE: "GET_UPDATED_BY_USER_FIREBASE",


  // *-- Firebase handlers
  ADD_CONTENT: "ADD_CONTENT",
  UPDATE_CONTENT: "UPDATE_CONTENT",
  SAVE_CONTENT: "SAVE_CONTENT",
  DELETE_CONNET: "DELETE_CONTENT"
} as const;

// --- card const
export const SEQUENCE_NAME = "MeForm" as const;
export const MAIN_CARD_NAME = "CardMeFormMain" as const;
export const LIST_CARD_NAME = "CardMeFormList" as const;
export const DETAIL_CARD_NAME = "CardMeFormDetail" as const;
export const BROADCAST_CARD_NAME = "CardMeFormBroadcast" as const;
export const BROADCAST_LIST_SEGMENT_NAME = "SegmentBroadcastList" as const;

// --- pagination const
export const DEFAULT_BROADCAST_LIST_LIMIT = 10;

// --- common const
export const ALL_BROADCAST_STATUSES: BroadcastStatus[] = [
  "BROADCASTED",
  "DRAFTED",
  "PENDING_REVIEW",
  "REVIEWED",
  "REVIEW_REJECTED"
];

export const COLLECTION = "mehealth_broadcast";

export const SITE_HOST = CONFIG.OFFICIAL_ACCOUNT_HOST;

export const MEHEALTH_BROADCAST_GS = CONFIG.FIREBASE_PROJECT === "mehealthportal" ? "gs://mehealth-broadcast-prod" : "gs://mehealth-broadcast";

/*================================ STATE ==============================*/

export type State = {
  MeFormSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;

    // * --- Form List Page
    formListPage: MeFormListPage;

    // * --- Form Detail Page
    formDetailPage: MeFormDetailPage;

    // * --- Firebase Handler
    firebaseData: BroadcastFirebaseData;

    // * --- common
    common: MeFormCommon;
  }> | null;
};

export type BroadcastFirebaseData = Partial<{
  selectedBroadcast: BroadcastDataFirebase;
  broadcastList: BroadcastDataFirebase[];
  loading: boolean;
}>;

export type MeFormListPage = Partial<{
  activeList: DiagFormListResponse;
  inactiveList: DiagFormListResponse;
}>;

export type MeFormDetailPage = Partial<{
  formId: number;
  formDetail: DiagRuleSerializer;
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  patientByLeafList: PatientByLeafList;
  broadcastNodeList: BroadcastNodeList;
  postCalculateCredit: PostCalculateCredit;
  broadcastDetail: BroadcastPostSerializer;
  createdByUser: EmployeeSerializer;
  updatedByUserFirebase: UpdatedUserFirebase;
}>;

export type MeFormCommon = Partial<{
  formCategoryList: FormCategorySerializer[];
}>;

export type DiagFormListResponse = Partial<{
  items: DiagRuleSerializer[];
  activePage: number;
  total: number;
}>;

export type BroadcastNodeList = Partial<{
  items: BroadcastPostSerializer[];
  activePage: number;
  total: number;
}>;

export type CreatedData = Partial<{
  name: string;
  form_type: DiagRuleSerializer["form_type"];
  category_name_list: string[];
  content: string;
  published: boolean;
}> | null;

export type QuestionNode = {
  id: number;
  parent_id: number;
  label: string;
  condition: string;
  text: string;
  type: string; // *
};

export type QuestionTree = Record<number, QuestionNode>;

export type SaveFormData = Partial<{
  active: boolean;
  content: string;
  published: boolean;
}>;

export type TreeNode = {
  id: number;
  parent_id: number | null;
  label: string;
  group_name: string;
  text: string;
  condition: string;
  type: string; // "root" | "choices" | "end"
  children?: TreeNode[];
  edit_user?: number;
  edit_user_name?: string;
};

export type FlatNode = {
  id: number;
  parent_id: number | null;
  label: string;
  group_name: string;
  condition: string;
  text: string;
  type: string;
  level: number;
  span: number;
  edit_user: number;
  edit_user_name: string;
};

export type ParsedNode = Record<string, FlatNode>;

export type MappedNode = Record<number, TreeNode>;

export type AllowedCondition = {
  tooManyImages: boolean;
  tooManyVideos: boolean;
  tooLongText: boolean;
};

export type BroadcastStatus = BroadcastPostSerializer["status"];

export type PostCalculateCredit = {
  total_member_count: number;
  credit_estimate_amount: string;
};

export type PatientByLeafResponse = {
  matched_patients: number;
  patient_ids: number[];
  patient_details: any[];
  filters: {
    published_diag_rule_id: number | null;
    diag_rule_id: number | null;
    leaf_node_id: number;
    leaf_node_label: string | null;
  };
};

export type PatientByLeafList = Record<number, PatientByLeafResponse | null>;

export type LatestWalletCredit = {
  active: boolean;
  balance: string;
  balance_updated_at: string;
  created_at: string;
  created_by: number | null;
  division: number | null;
  edited_at: string | null;
  edited_by: number | null;
  id: number;
};

export type CreatedBroadcastData = {
  name: string;
  status: BroadcastPostSerializer["status"];
  receiver_type: BroadcastPostSerializer["receiver_type"];
  broadcast_type: BroadcastPostSerializer["broadcast_type"];
  schedule_broadcasted_at?: string;
  published_diag_rule_nodes: number[];
  content: string;
};

export type ExtraCreatedBroadcastData = {
  firebase_id: string;
  division: number;
};

export type CreatedBroadcastDataApi = CreatedBroadcastData & ExtraCreatedBroadcastData;

export type BroadcastListType = {
  id: string;
  data: BroadcastDataFirebase;
};

export type BroadcastDataFirebase = Partial<{
  is_active: boolean;
  id: string;
  published_at: Timestamp | FieldValue;
  updated_by: Partial<{ id: number; name: string }>;
  organ_tags: any[];
  created_at: Timestamp | FieldValue;
  previews_picture: any[];
  division_id: number;
  previews_video: any[];
  author_id: number;
  published_version: number;
  updated_at: Timestamp | FieldValue;
  content: string;
  isEdit: boolean;
  status: string;
  version: number;
  content_storage: ContentStorage
  previews_text: string;
  shorthen_text: string;
}>;

export type ContentStorage = {
  content_hash: string;
  bucket_path: string;
  content_size: number;
  content_url: string;
};

export type UpdatedUserFirebase = Partial<{
  id: number;
  name: string;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "loadingStatus" | "django" | "BillingCreditWallet" | "masterOptions" | "userProfile"
>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> };

export type Data = Partial<{
  division: number;
  masterData?: any;
  userProfile?: UserProfileSerializer & { full_name: string };
}>;

export type Actions = typeof ACTIONS;

export type SequenceName = typeof SEQUENCE_NAME;

export type SeqState = BaseSeqState<SequenceName>;

/*================================ GENERIC ==============================*/

export type FormType = "DECISION_TREE" | "INPUT_FORM";

/*================================ PARAMS ==============================*/

export type FetchFormParams = Partial<{
  detailData: DiagRuleSerializer;
  pdrNodeData: PublishedDiagRuleNodeSerializer[];
  division: number;
  activePage: number;
  inactivePage: number;
}>;

export type AddFormParams = Partial<{
  createdData: CreatedData;
  division: number;
  onSuccess: () => void;
}>;

export type ActionFormDetailParams = {
  formId: number;
  data: SaveFormData;
  division?: number;
};

export type EditFormParams = {
  formId: number;
  editedData?: CreatedData;
  division?: number;
  onSuccess?: () => void;
};

export type BroadcastNodeParams = {
  published_diag_rule_node: number;
  statuses: BroadcastStatus | BroadcastStatus[];
};

export type BroadcastNodeListParams = {
  broadcastNodeParams: BroadcastNodeParams;
  activePage?: number;
};

export type PostCalculateCreditParams = {
  receiver_type: BroadcastPostSerializer["receiver_type"];
  published_diag_rule_nodes: number[];
};

export type UpdateContentParams = Partial<{
  content: string;
  id: string; // firebase id
  targetImgElement: HTMLImageElement;
  index: number;
  contentAction: "create" | "update" | "delete";
  imageInfo: any;
  remaingingFilesCount: number;
  files: File[];
  info: object;
}>;

export type FormCategoryParams = Partial<{
  name: string;
  division: number;
}>;

export type ActionPayloadMap = {
  [ACTIONS.FETCH]: FetchFormParams;

  // * -- List Page
  [ACTIONS.ADD_FORM]: AddFormParams;
  [ACTIONS.GET_FORM]: { formId: number };
  [ACTIONS.ACTION_FORM]: ActionFormDetailParams;
  [ACTIONS.DOWNLOAD_RAW]: { formId: number };

  // * -- Detail Page
  [ACTIONS.PUBLISH_FORM]: ActionFormDetailParams;
  [ACTIONS.SAVE_FORM]: ActionFormDetailParams;
  [ACTIONS.EDIT_FORM]: EditFormParams;
  [ACTIONS.GET_BROADCAST_NODE_LIST]: BroadcastNodeListParams;
  [ACTIONS.GET_POST_CALCULATE_CREDIT]: { calculateParams: PostCalculateCreditParams };
  [ACTIONS.POST_BROADCAST]: { createdData: CreatedBroadcastData };
  [ACTIONS.GET_UPDATED_BY_USER_FIREBASE]: { firebaseId: string };

  // * -- Firebase Handlers
  [ACTIONS.UPDATE_CONTENT]: UpdateContentParams;
};

export type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

export type LoadingKey = keyof PickedState["loadingStatus"];

export type ButtonLoadCheckKey = keyof PickedState["buttonLoadCheck"];

export type ErrorMessageKey = keyof PickedState["errorMessage"];

export type SuccessMessageKey = keyof PickedState["successMessage"];

export type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

export type Controller = WasmController<PickedState & State, Event, Data, {}>;

export type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/*================================ HELPER FUNCS ==============================*/

export const setLoading = (
  controller: Controller,
  key: LoadingKey,
  loading: boolean
) => {
  controller.setState((prev) => ({
    loadingStatus: {
      ...prev.loadingStatus,
      [key]: loading,
    },
  }));
};

export const setButtonLoadCheck = (
  controller: Controller,
  key: ButtonLoadCheckKey,
  status: BLC_STATUS_TYPE | null
) => {
  controller.setState((prev) => ({
    buttonLoadCheck: {
      ...prev.buttonLoadCheck,
      [key]: status,
    },
  }));
};

export const setErrorMessage = (
  controller: Controller,
  key: ErrorMessageKey,
  error: any
) => {
  controller.setState((prev) => ({
    errorMessage: {
      ...prev.errorMessage,
      [key]: error,
    },
  }));
};

export const setSuccessMessage = (
  controller: Controller,
  key: SuccessMessageKey,
  message: any
) => {
  controller.setState((prev) => ({
    successMessage: {
      ...prev.successMessage,
      [key]: message,
    },
  }));
};
